import {
  MapLayer,
  MapSource,
  sourceAttributionHtml,
} from "@cartographerio/atlas-map";
import { AccessToken, WorkspaceRef } from "@cartographerio/types";
import { checkExhausted } from "@cartographerio/util";
import { Feature, FeatureCollection } from "geojson";
import { MD5 } from "object-hash";
import { defaultCartographerApiUrl } from "../env";
import { MapboxSource, MapboxSources } from "../mapbox";

interface CartographerMapUrlOptions {
  apiUrl?: string;
  simplify?: boolean;
  accessToken?: AccessToken;
  workspace?: WorkspaceRef;
}

function cartographerMapUrl(
  layerId: string,
  options: CartographerMapUrlOptions = {}
): string {
  const { simplify, apiUrl, workspace, accessToken } = options;

  const baseUrl = apiUrl ?? defaultCartographerApiUrl;

  const query = [];

  if (simplify != null) {
    query.push(`simplify=${simplify}`);
  }

  if (workspace != null) {
    query.push(`workspace=${encodeURIComponent(workspace)}`);
  }

  if (accessToken != null) {
    query.push(`authorization=${encodeURIComponent(accessToken)}`);
  }

  return `${baseUrl}/v1/map/${layerId}?${query.join("&")}`;
}

function cartographerTileUrl(
  layerId: string,
  options: CartographerMapUrlOptions = {}
): string {
  const { simplify, apiUrl, workspace, accessToken } = options;

  const baseUrl = apiUrl ?? defaultCartographerApiUrl;

  const query = [];

  if (simplify != null) {
    query.push(`simplify=${simplify}`);
  }

  if (workspace != null) {
    query.push(`workspace=${encodeURIComponent(workspace)}`);
  }

  if (accessToken != null) {
    query.push(`authorization=${encodeURIComponent(accessToken)}`);
  }

  return `${baseUrl}/v1/map/${layerId}/tile/{z}/{x}/{y}?${query.join("&")}`;
}

function mapboxGeojsonSource(
  data: Feature | FeatureCollection | string,
  attribution: string | undefined
): MapboxSource {
  // Mapbox expects the `attribution` field to be a string.
  // If it is `undefined`, style validation fails.
  if (attribution == null) {
    return {
      type: "geojson",
      data,
    };
  } else {
    return {
      type: "geojson",
      data,
      attribution,
    };
  }
}

function mapboxTileSourceFromTileJsonUrl(
  url: string,
  attribution: string | undefined
): MapboxSource {
  // Mapbox expects the `attribution` field to be a string.
  // If it is `undefined`, style validation fails.
  if (attribution == null) {
    return {
      type: "vector",
      url,
    };
  } else {
    return {
      type: "vector",
      url,
      attribution,
    };
  }
}

function mapboxTileSourceFromTilesUrl(
  tiles: string,
  attribution: string | undefined
): MapboxSource {
  // Mapbox expects the `attribution` field to be a string.
  // If it is `undefined`, style validation fails.
  if (attribution == null) {
    return {
      type: "vector",
      tiles: [tiles],
    };
  } else {
    return {
      type: "vector",
      tiles: [tiles],
      attribution,
    };
  }
}

function normalizePunctuation(str: string): string {
  return str.replaceAll(/[^A-Za-z0-9-_]/g, "_");
}

export function mapboxSourceId(
  source: MapSource,
  simplified: boolean = false
): string {
  switch (source.type) {
    case "CartographerSource":
      switch (source.forceSourceType) {
        case "tiles":
          return normalizePunctuation(
            simplified
              ? `cartographerTile/${source.layerId}/simplified`
              : `cartographerTile/${source.layerId}`
          );

        case "geojson":
        case undefined:
          return normalizePunctuation(
            simplified
              ? `cartographerGeojson/${source.layerId}/simplified`
              : `cartographerGeojson/${source.layerId}`
          );

        default:
          return checkExhausted(source.forceSourceType);
      }

    case "RemoteGeojsonSource":
      return normalizePunctuation(
        simplified
          ? `remoteGeojson/${source.url}/simplified`
          : `remoteGeojson/${source.url}`
      );

    case "RemoteTileSource":
      return normalizePunctuation(
        simplified
          ? `remoteVectorTile/${source.url}/simplified`
          : `remoteVectorTile/${source.url}`
      );

    case "LocalGeojsonSource":
      return normalizePunctuation(
        simplified
          ? `localGeojson/${MD5(source.data)}/simplified`
          : `localGeojson/${MD5(source.data)}`
      );

    default:
      return checkExhausted(source);
  }
}

export function mapboxSourceLayerId(source: MapSource): string | undefined {
  switch (source.type) {
    case "CartographerSource":
      switch (source.forceSourceType) {
        case "tiles":
          return "default";

        case "geojson":
        case undefined:
          return undefined;

        default:
          return checkExhausted(source.forceSourceType);
      }

    case "RemoteGeojsonSource":
    case "LocalGeojsonSource":
      return undefined;

    case "RemoteTileSource":
      return source.sourceLayer;

    default:
      return checkExhausted(source);
  }
}

export function mapboxSourceIds(layer: MapLayer): string[] {
  const { source } = layer;

  switch (source.type) {
    case "RemoteGeojsonSource":
    case "RemoteTileSource":
    case "LocalGeojsonSource":
      return [mapboxSourceId(source)];

    case "CartographerSource":
      return source.simplifyZoom == null
        ? [mapboxSourceId(source)]
        : [mapboxSourceId(source), mapboxSourceId(source, true)];

    default:
      return checkExhausted(source);
  }
}

export interface CreateMapboxSourcesOptions {
  apiUrl?: string;
  accessToken?: AccessToken;
  defaultWorkspace?: WorkspaceRef;
}

export function createMapboxSources(
  layer: MapLayer,
  options: CreateMapboxSourcesOptions = {}
): MapboxSources {
  const { source } = layer;
  const { apiUrl, defaultWorkspace: workspace, accessToken } = options;

  const sourceId = mapboxSourceId(source);

  const ans: MapboxSources = {};

  // Add a source for the original data:

  switch (source.type) {
    case "RemoteGeojsonSource":
      ans[sourceId] = mapboxGeojsonSource(
        source.url,
        sourceAttributionHtml(source)
      );
      break;

    case "RemoteTileSource":
      ans[sourceId] = mapboxTileSourceFromTileJsonUrl(
        source.url,
        sourceAttributionHtml(source)
      );
      break;

    case "LocalGeojsonSource":
      ans[sourceId] = mapboxGeojsonSource(
        source.data,
        sourceAttributionHtml(source)
      );
      break;

    case "CartographerSource":
      switch (source.forceSourceType) {
        case "tiles":
          // Regular geometry:
          ans[sourceId] = mapboxTileSourceFromTilesUrl(
            cartographerTileUrl(source.layerId, {
              simplify: false,
              apiUrl,
              workspace,
              accessToken,
            }),
            sourceAttributionHtml(source)
          );

          // Simplified geometry:
          if (source.simplifyZoom != null) {
            ans[mapboxSourceId(source, true)] = mapboxTileSourceFromTilesUrl(
              cartographerTileUrl(source.layerId, {
                simplify: true,
                apiUrl,
                workspace,
                accessToken,
              }),
              sourceAttributionHtml(source)
            );
          }

          break;

        case "geojson":
        case undefined:
          // Regular geometry:
          ans[sourceId] = mapboxGeojsonSource(
            cartographerMapUrl(source.layerId, {
              simplify: false,
              apiUrl,
              workspace,
              accessToken,
            }),
            sourceAttributionHtml(source)
          );

          // Simplified geometry:
          if (source.simplifyZoom != null) {
            ans[mapboxSourceId(source, true)] = mapboxGeojsonSource(
              cartographerMapUrl(source.layerId, {
                simplify: true,
                apiUrl,
                workspace,
                accessToken,
              }),
              sourceAttributionHtml(source)
            );
          }

          break;

        default:
          checkExhausted(source.forceSourceType);
      }
      break;

    default:
      checkExhausted(source);
  }

  return ans;
}
